import { get, isArray, isObject, isString, compact } from 'lodash';

function parseJsonApiCompliantErrors(errors) {
  const parsedErrors = errors.map((e) => {
    const field = get(e, 'meta.humanized');

    // Only prepend the field name if it points to something descriptive.
    return field.toLowerCase() === 'base' ? e.detail : `${field} ${e.detail}`;
  });

  return compact(parsedErrors);
}

// TODO: Rename this since it doesn't only handle server errors. It handles all
// types even client-side runtime errors such as `NetworkError`.
export function parseServerErrors(errorObject) {
  const contentType = get(errorObject, 'response.headers.content-type');
  const status = get(errorObject, 'response.status');
  const statusText = get(errorObject, 'response.statusText');

  // Hides ugly stack traces in development when an endpoint does not return
  // JSON. This typically happens with 500 errors for Rails in development. Or
  // for 404 with missing endpoints.
  if (contentType && contentType.indexOf('json') === -1) {
    return [`${status} ${statusText}`];
  }

  // Production Rails should be automatically hiding these anyway.
  if (isServerError(errorObject)) {
    return ['Server error occurred'];
  }

  const errors = get(errorObject, 'response.data.errors');

  if (isArray(errors)) {
    if (errors.length === 0) {
      return errors;
    }

    if (errors.length === 1) {
      if (errors[0].message) {
        // We prefer to use message if it's available. For example:
        // `[{ status: 422, title: 'Unprocessable Entity', message: 'Invalid credit card number' }]`
        return [errors[0].message];
      } else if (errors[0].status && errors[0].title) {
        // During login, we can get a format that looks like
        // `[{ status: 500, title: 'Internal Server Error' }]`
        return [`${errors[0].status} ${errors[0].title}`];
      }
    }

    // JSON:API compliant errors with the following format:
    // ```
    //   [
    //     {
    //       detail: 'cant be blank',
    //       source: { pointer: '/data/attributes/code' },
    //       title: 'blank'
    //     }
    //  ]
    // ```
    if (errors[0].source) {
      return parseJsonApiCompliantErrors(errors);
    }

    // Errors from `devise_token_auth` with this format:
    // `['Invalid login credentials. Please try again']`
    return errors;
  }

  // Validation errors for a record.
  // TODO: Still relevant? Errors should be coming back in JSON:API format.
  if (isObject(errors) && errors.full_messages) {
    return errors.full_messages;
  }

  // Rack-attack returns plain string responses:
  const data = get(errorObject, 'response.data');
  if (isString(data)) {
    return [data.trim()];
  }

  // If we got here, this is probably a runtime error like NetworkError.
  if (errorObject.message) {
    return [errorObject.message];
  }
}

export function isApiError(error) {
  // TODO: Consider making this smarter. Check for status code?
  return !!error.response;
}

export function isServerError(error) {
  const status = error.response?.status;
  return status >= 500;
}
